import { type ReactNode, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Pencil } from 'lucide-react'
import { useViewOnly } from '@/components/templates/app-shell'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ApiError, getJSON } from '@/lib/api'
import { MemberSignInCopy } from './MemberSignInCopy'
import { MemberSignInDialog } from './MemberSignInDialogs'
import { useMemberSignIn } from './useMemberSignIn'
import type { MemberSignInState } from './memberSignInTypes'

type Seat = {
  id: number
  user: { id: number }
  association: { id: number }
  roles: string[]
  staff_roles: string[]
}
// `filledAllowed` is false when the page already shows a filled button (DESIGN.md: a panel
// header button is filled only when no other filled button is visible on the screen).
export function MemberSignInCard({
  connectionId,
  filledAllowed = true,
}: {
  connectionId: number
  filledAllowed?: boolean
}) {
  const viewOnly = useViewOnly()
  const seat = useQuery({
    queryKey: ['seat'],
    queryFn: () => getJSON<{ seat: Seat }>('/api/v1/seat'),
    retry: false,
  })
  if (!seat.data?.seat || seat.isError) return null
  const scope = JSON.stringify([seat.data.seat, viewOnly])
  return (
    <ResolvedCard
      key={`${connectionId}:${scope}`}
      id={connectionId}
      scope={scope}
      readOnly={viewOnly}
      filledAllowed={filledAllowed}
    />
  )
}
function ResolvedCard({
  id,
  scope,
  readOnly,
  filledAllowed,
}: {
  id: number
  scope: string
  readOnly: boolean
  filledAllowed: boolean
}) {
  const action = useMemberSignIn(id, scope, readOnly)
  const state = action.query.data
  if (action.query.isError) {
    const error = action.query.error
    return <ReadError error={error} />
  }
  if (!state?.supported) return null
  const canManage = state.can_manage && !readOnly
  return (
    <TooltipProvider>
      <Card role="region" aria-label="Member sign-in">
        <SignInHeader
          state={state}
          canManage={canManage}
          filledAllowed={filledAllowed}
          action={action}
        />
        {!state.enabled && (
          <p className="px-4 py-3 text-caption text-muted-foreground">
            Members see no link to this site.
          </p>
        )}
        {action.error && !action.dialog && <Alert className="m-3">{action.error}</Alert>}
        {action.secret && canManage && (
          <SecretNotice secret={action.secret} onDone={action.dismissSecret} />
        )}
        {state.client_id && (
          <SignInFacts
            state={state}
            canManage={canManage}
            pending={action.pending}
            edit={() => action.open('link')}
          />
        )}
        <SetupSteps setup={state.setup} bordered={!!state.client_id} />
      </Card>
      {action.dialog && canManage && (
        <MemberSignInDialog
          kind={action.dialog}
          state={state}
          pending={action.pending}
          error={action.error}
          onClose={action.close}
          onSave={(change) => void action.save(change)}
        />
      )}
    </TooltipProvider>
  )
}
function ReadError({ error }: { error: Error }) {
  return error instanceof ApiError && [401, 403, 404].includes(error.status) ? null : (
    <Alert>{error.message}</Alert>
  )
}
function SignInHeader({
  state,
  canManage,
  filledAllowed,
  action,
}: {
  state: MemberSignInState
  canManage: boolean
  filledAllowed: boolean
  action: ReturnType<typeof useMemberSignIn>
}) {
  return (
    <div className="flex min-h-12 flex-wrap items-center gap-2 border-b border-border px-4 py-2 sm:h-12 sm:flex-nowrap sm:py-0">
      <h2 className="whitespace-nowrap text-body-sm font-medium">Member sign-in</h2>
      <Badge variant={state.enabled ? 'success' : 'neutral'}>{state.enabled ? 'On' : 'Off'}</Badge>
      {canManage && (
        <div className="ml-auto flex gap-2">
          {state.enabled && (
            <Button
              variant="outline"
              size="sm"
              className="whitespace-nowrap"
              disabled={action.pending}
              onClick={() => action.open('secret')}
            >
              Issue new secret
            </Button>
          )}
          <Button
            variant={state.enabled || !filledAllowed ? 'outline' : 'default'}
            size="sm"
            className="whitespace-nowrap"
            disabled={action.pending}
            onClick={() => void action.save({ enabled: !state.enabled })}
          >
            {state.enabled ? 'Turn off' : 'Turn on'}
          </Button>
        </div>
      )}
    </div>
  )
}
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="flex h-9 items-center px-4 text-caption text-muted-foreground">{label}</dt>
      <dd className="flex h-9 min-w-0 items-center gap-2 px-4 text-body-sm">{children}</dd>
    </>
  )
}
function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <Fact label={label}>
      <span className="truncate font-mono" title={value}>
        {value}
      </span>
      <MemberSignInCopy value={value} label={`Copy ${label}`} />
    </Fact>
  )
}
function SignInFacts({
  state,
  canManage,
  pending,
  edit,
}: {
  state: MemberSignInState
  canManage: boolean
  pending: boolean
  edit: () => void
}) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] content-start [&>*]:border-border [&>dd:not(:nth-child(2))]:border-t [&>dt:not(:first-child)]:border-t">
      <CopyRow label="Client ID" value={state.client_id!} />
      {state.enabled && (
        <>
          <Fact label="Client secret">
            <span className="font-mono">••••••••••••••••</span>
          </Fact>
          <CopyRow label="Issuer address" value={state.issuer_address} />
          <CopyRow label="Return address" value={state.return_address} />
          <Fact label="Link address">
            <span className="truncate" title={state.link_address}>
              {state.link_address}
            </span>
            {canManage && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit link address"
                    disabled={pending}
                    onClick={edit}
                  >
                    <Pencil className="size-3.5" strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit link address</TooltipContent>
              </Tooltip>
            )}
          </Fact>
        </>
      )}
    </dl>
  )
}
function SecretNotice({ secret, onDone }: { secret: string; onDone: () => void }) {
  return (
    <div className="border-b border-border">
      <Alert variant="warning" className="m-4 space-y-3">
        <div>
          <p className="font-medium">Copy the client secret now</p>
          <p className="text-body-sm">It is not shown again.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 select-all break-all rounded-md border border-border bg-background px-2 py-1 font-mono text-body-sm">
            {secret}
          </code>
          <MemberSignInCopy value={secret} label="Copy" text />
          <Button variant="ghost" onClick={onDone}>
            Done
          </Button>
        </div>
      </Alert>
    </div>
  )
}
function SetupSteps({ setup, bordered }: { setup: MemberSignInState['setup']; bordered: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={bordered ? 'border-t border-border' : ''}
    >
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="m-2 gap-1.5 px-2 py-2 text-body-sm">
          <ChevronDown
            className={`size-4 transition-transform ${open ? '' : '-rotate-90'}`}
            strokeWidth={1.5}
          />
          {setup.heading}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className="max-w-prose list-decimal space-y-1.5 pb-4 pl-10 pr-4 pt-2 text-body-sm">
          {setup.steps.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </ol>
        <p className="max-w-prose px-4 pb-4 text-body-sm">{setup.note}</p>
      </CollapsibleContent>
    </Collapsible>
  )
}
